import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { shallow } from 'zustand/shallow';

import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { Button } from 'src/general/components/Button/Button';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { ObserverValue, initVisibilityObserver } from 'src/general/helpers/initVisibilityObserver';
import { CarDisplayState } from 'src/pdp/sections/Pdp/pdpHelpers';
import { authStore } from 'src/stores/authStore';
import { getMicrocopy } from 'src/stores/microcopyStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { PRICE_CARD_IDS } from '../CarPriceCard/CarPriceCard';
import { CarPriceCardDetails } from '../CarPriceCard/CarPriceCardDetails';
import styles from './CarStickyBar.module.scss';

const TRANSITION_DURATION = parseInt(styles['transitionDuration'], 10);

interface Props {
  priceCardId: string;
  formId: string;
  displayState: CarDisplayState;
  isCheckoutProgress: boolean;
  productDetails: ProductDetailsPayload;
  onOpenCarEnquiryForm: (entryPoint: string) => void;
  /** Test-only property */
  overwriteScrollSpy?: boolean;
}

export const CarStickyBar: FC<Props> = ({
  priceCardId,
  isCheckoutProgress,
  displayState,
  productDetails,
  overwriteScrollSpy = false,
  onOpenCarEnquiryForm,
}) => {
  const flags = useFlags();
  const { isUserLoggedIn } = authStore(
    (state) => ({
      isUserLoggedIn: state.isUserLoggedIn,
    }),
    shallow,
  );
  const href = isCheckoutProgress
    ? `/checkout?sku=${productDetails.sku}`
    : `/product-detail/${productDetails.sku}/get-started`;
  const carData = productDetailPayloadToBaseCarData(productDetails);
  const financePrice = formatNumber(productDetails.weeklyRepayment, { prepend: '$', append: '/wk' });
  const startCheckoutDefault = getMicrocopy(MICROCOPY.GENERAL_CTA_START_CHECKOUT);
  const startCheckout = flags?.experimentPdpCtaButton ?? startCheckoutDefault;
  const continueCheckout = getMicrocopy(MICROCOPY.GENERAL_HEADER_PURCHASE_PROGRESS_BUTTON);
  const notAvailable = getMicrocopy(MICROCOPY.GENERAL_CTA_NOT_AVAILABLE);
  const message = getMicrocopy(MICROCOPY.GENERAL_CTA_MESSAGE);
  const financeData = getMicrocopy(MICROCOPY.PDP_PRICE_CARD_FINANCE, true, {
    price: financePrice,
    interestRate: formatNumber(productDetails.defaultRate * 100, { append: '% p.a.', decimals: 2 }),
  });
  const financeDataMobile = getMicrocopy(MICROCOPY.PDP_PRICE_CARD_FINANCE_SHORT, true, {
    price: financePrice,
  });
  const [isVisible, setIsVisible] = useState<boolean>(!!overwriteScrollSpy);
  const unsubscribe = useRef<(() => void) | null>(null);
  const [keyboardOpen, setKeyboardOpen] = useState<boolean>(false);
  const onStartCheckout = useCallback(() => {
    pushToDataLayer({ event: 'checkout_attempt', logged_in: isUserLoggedIn });
  }, [isUserLoggedIn]);

  const initKeyboardVisibility = async () => {
    const { isSupported, subscribe } = await import('on-screen-keyboard-detector');
    if (isSupported()) {
      unsubscribe.current = subscribe((visibility) => {
        setKeyboardOpen(visibility === 'visible');
      });
    }
  };

  const handleBarVisibility = useCallback(
    (value: ObserverValue) => {
      if (!overwriteScrollSpy) {
        const visible = value === 'below';
        setIsVisible(visible);
      }
    },
    [overwriteScrollSpy, setIsVisible],
  );

  useEffect(() => {
    initKeyboardVisibility();
    return () => {
      if (unsubscribe.current) {
        unsubscribe.current();
      }
    };
  });

  useEffect(() => {
    const observer = initVisibilityObserver(
      priceCardId,
      handleBarVisibility,
      118, // height of the header menu
    );

    return () => {
      observer?.disconnect();
    };
  }, [handleBarVisibility]);

  // aria-hidden is present on the element as it's not required for screen readers
  return (
    <CSSTransition
      in={isVisible}
      appear
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        exit: styles.exit,
        exitActive: styles.exitActive,
      }}
      unmountOnExit
      timeout={TRANSITION_DURATION}
    >
      <aside className={classNames(styles.root, { [styles.keyboardOpen]: keyboardOpen })} aria-hidden>
        <div className={styles.content}>
          <header className={styles.carData}>
            <h2 className={styles.title}>{carData.model}</h2>
            <CarPriceCardDetails carData={carData} className={styles.carDataList} />
          </header>
          <div className={styles.price} aria-describedby={PRICE_CARD_IDS.PRICE}>
            {formatNumber(carData.price, { prepend: '$', append: '*' })}
          </div>
          <div className={styles.financePrice} aria-describedby={PRICE_CARD_IDS.FINANCE}>
            {financeData}
          </div>
          <div className={styles.financePriceMobile} aria-describedby={PRICE_CARD_IDS.FINANCE}>
            {financeDataMobile}
          </div>
          <div className={styles.controls}>
            {(displayState === 'available' || displayState === 'continue-purchase' || displayState === 'loading') && (
              <>
                <Button
                  onClick={onStartCheckout}
                  href={displayState === 'loading' ? undefined : href}
                  isLoading={displayState === 'loading'}
                  isDisabledWhileLoading
                  variant="accent"
                >
                  {displayState === 'continue-purchase' ? continueCheckout : startCheckout}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => onOpenCarEnquiryForm('sticky_cta')}
                  data-testid="carStickyBarMessage"
                >
                  {message}
                </Button>
              </>
            )}
            {displayState === 'not-available' && (
              <Button
                variant="primary"
                href={`/product-detail/${carData.sku}/similar`}
                data-testid="carStickyBarSimilarCars"
              >
                {notAvailable}
              </Button>
            )}
          </div>
        </div>
      </aside>
    </CSSTransition>
  );
};
